import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Instagram, Loader2, Send } from "lucide-react";
import { Reveal } from "./Reveal";
import { waLink, INSTAGRAM_URL, WHATSAPP_NUMBER_DISPLAY } from "./constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MORTGAGE_TYPES = [
  "First-time buyer",
  "Remortgage",
  "Buy-to-let",
  "Home mover",
  "Not sure yet",
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-[#03222D]/60 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors focus:border-amber-400/60";

export default function EnquiryForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    mortgage_type: "First-time buyer",
    property_value: "",
    deposit: "",
    contact_method: "whatsapp",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Just pop your name, number and email in and you're good to go.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        mortgage_type: form.mortgage_type,
        property_value: form.property_value ? Number(form.property_value) : null,
        deposit: form.deposit ? Number(form.deposit) : null,
        contact_method: form.contact_method,
        message: form.message.trim() || null,
      });
      setSent(true);
      toast.success("Thanks — I'll be in touch shortly.");
    } catch {
      toast.error("That didn't send, sorry — try me on WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const waText = `Hi Kieran, I'm ${form.name || "thinking about a mortgage"} (${form.mortgage_type}).${
    form.property_value ? ` The property's around £${form.property_value}.` : ""
  } Can we chat?`;

  return (
    <section id="enquire" data-testid="enquiry-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14">
        <div>
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-amber-400/90 mb-5">
              Say Hello
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight font-medium text-white leading-tight">
              Fancy a chat about your plans?
            </h2>
            <p className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed max-w-md">
              Tell me a bit about what you're hoping to do and I'll come back
              with honest advice — no obligation, no jargon, and no pushy
              sales chat.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 space-y-4 max-w-md">
              <a
                href={waLink("Hi Kieran, fancy a chat about my mortgage options?")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enquiry-whatsapp-card"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#073B4C] p-5 transition-all duration-300 hover:border-[#25D366]/50 hover:-translate-y-0.5"
              >
                <span className="h-11 w-11 rounded-xl bg-[#25D366]/15 border border-[#25D366]/40 flex items-center justify-center">
                  <MessageCircle size={19} className="text-[#25D366]" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">WhatsApp {WHATSAPP_NUMBER_DISPLAY}</span>
                  <span className="block text-xs text-slate-500 mt-0.5">The quickest way to reach me — I usually reply within the hour</span>
                </span>
              </a>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enquiry-instagram-card"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#073B4C] p-5 transition-all duration-300 hover:border-amber-400/50 hover:-translate-y-0.5"
              >
                <span className="h-11 w-11 rounded-xl bg-amber-400/10 border border-amber-400/30 flex items-center justify-center">
                  <Instagram size={19} className="text-amber-400" />
                </span>
                <span>
                  <span className="block text-sm font-semibold text-white">@kieranwoolleymortgages</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Tips, rate updates and happy clients over on Instagram</span>
                </span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="enquiry-form"
            className="rounded-3xl border border-white/10 bg-[#073B4C] p-8 lg:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">Your name *</label>
                <input
                  value={form.name}
                  onChange={set("name")}
                  placeholder="What should I call you?"
                  className={inputCls}
                  data-testid="enquiry-name-input"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">Phone *</label>
                <input
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="07..."
                  type="tel"
                  className={inputCls}
                  data-testid="enquiry-phone-input"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">Email *</label>
                <input
                  value={form.email}
                  onChange={set("email")}
                  placeholder="So I can confirm I've got your enquiry"
                  type="email"
                  className={inputCls}
                  data-testid="enquiry-email-input"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">What are you planning?</label>
                <select
                  value={form.mortgage_type}
                  onChange={set("mortgage_type")}
                  className={inputCls}
                  data-testid="enquiry-type-select"
                >
                  {MORTGAGE_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-[#073B4C]">{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">Property value (£)</label>
                <input
                  value={form.property_value}
                  onChange={set("property_value")}
                  placeholder="350000"
                  type="number"
                  min="0"
                  className={inputCls}
                  data-testid="enquiry-value-input"
                />
              </div>
              <div>
                <label className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">Deposit (£)</label>
                <input
                  value={form.deposit}
                  onChange={set("deposit")}
                  placeholder="50000"
                  type="number"
                  min="0"
                  className={inputCls}
                  data-testid="enquiry-deposit-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">How should I get back to you?</label>
                <div className="flex gap-3">
                  {[
                    ["whatsapp", "WhatsApp"],
                    ["phone", "Phone call"],
                  ].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, contact_method: val }))}
                      data-testid={`enquiry-contact-${val}`}
                      className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                        form.contact_method === val
                          ? "border-amber-400/60 bg-amber-400/10 text-amber-300"
                          : "border-white/10 bg-[#03222D]/60 text-slate-400 hover:border-white/25"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-mono tracking-wider uppercase text-slate-500 mb-2">Anything else? (optional)</label>
                <textarea
                  value={form.message}
                  onChange={set("message")}
                  rows={3}
                  placeholder="Timeline, location, questions…"
                  className={`${inputCls} resize-none`}
                  data-testid="enquiry-message-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="enquiry-submit-button"
              className="mt-7 w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-semibold text-[#03222D] transition-all duration-300 hover:bg-amber-300 disabled:opacity-60"
            >
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Sending…" : "Send It Over to Kieran"}
            </button>
            {sent && (
              <a
                href={waLink(waText)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="enquiry-whatsapp-followup"
                className="mt-4 w-full inline-flex items-center justify-center gap-2.5 rounded-full border border-[#25D366]/50 bg-[#25D366]/10 px-7 py-3.5 text-sm font-semibold text-[#25D366] transition-colors hover:bg-[#25D366]/20"
              >
                <MessageCircle size={16} />
                Sent! Carry on in WhatsApp if you like
              </a>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}
